/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { TableColumnsType, Button, Typography } from 'antd';
import { Table } from '@/components/tables/table';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
    getListHealthStatus,
    watchHealthStatusList,
    watchHealthStatusPagination,
    watchHealthStatusLoading,
} from '@/redux/slices/health-status.slice';
import { useCallback, useEffect } from 'react';
import { PAGINATION_PARAMS } from '@/constants/pagination.constant';

type ListHealthStatusTableProps = {
    setActiveTabProps: React.Dispatch<React.SetStateAction<string>>;
};

type PageChangeParams = {
    page?: number;
    pageSize?: number;
};

export default function ListHealthStatusTable({ setActiveTabProps }: ListHealthStatusTableProps) {
    const dispatch = useAppDispatch();
    const healthStatusList = useAppSelector(watchHealthStatusList);
    const pagination = useAppSelector(watchHealthStatusPagination);
    const loading = useAppSelector(watchHealthStatusLoading);

        const handlePageChange = useCallback(
        ({
            page = PAGINATION_PARAMS.offset,
            pageSize = PAGINATION_PARAMS.limit,
        }: PageChangeParams) => {
            const offset = (page - 1) * pageSize;
            dispatch(
                getListHealthStatus({
                    offset,
                    limit: pageSize,
                })
            );
        },
        [dispatch]
    );

    useEffect(() => {
        handlePageChange({});
    }, [handlePageChange]);

    const tableData = healthStatusList?.map((item: HealthStatus, _index: number) => ({
        key: item.healthStatusId,
        healthStatusId: item.healthStatusId,
        displayName: item.displayName,
    })) || [];

    const currentPage = Math.floor(pagination.curPage / pagination.limitPage) + 1;

    const columns: TableColumnsType<HealthStatus> = [
        {
            title: 'STT',
            render: (_, __, index) => pagination.curPage + index + 1,
            width: 80,
        },
        {
            title: 'ID tình trạng sức khỏe',
            dataIndex: 'healthStatusId',
            key: 'healthStatusId',
            width: 200,
        },
        {
            title: 'Tình trạng sức khỏe',
            dataIndex: 'displayName',
            key: 'displayName',
        },
    ];

    return (
        <div css={boxStyles}>
            <div css={headerStyles}>
                <Typography.Title level={5}>Danh sách các tình trạng sức khỏe</Typography.Title>
                <Button
                    onClick={() => setActiveTabProps('IMPORT_HS_BY_LIST')}
                    css={listInputButtonStyles}
                >
                    Nhập tình trạng sức khỏe theo danh sách
                </Button>
            </div>

            <Table<any>
                columns={columns}
                dataSource={tableData}
                pagination={{
                    current: currentPage,
                    pageSize: pagination.limitPage,
                    total: pagination.totalRows,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50', '100'],
                    onChange: (page, pageSize) => {
                        handlePageChange({ page, pageSize });
                    }
                }}
                loading={loading}
            />
        </div>
    );
}

const boxStyles = css`
  padding: 1rem;
  background: var(--white-color);
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
  &:nth-of-type(2) {
    padding: 1.6rem 1rem;
  }

  .ant-table-thead > tr > th {
    background: var(--blue-dark-color) !important;
    color: var(--white-color);
  }

  .ant-table-cell::before {
    display: none;
  }
`;

const listInputButtonStyles = css`
  background-color: var(--success-color);
  color: var(--white-color);
  border-color: var(--success-color);

  &:hover {
    background-color: var(--success-color) !important;
    border-color: var(--success-color) !important;
  }
`;

const headerStyles = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;